/**
 * The contact step's form — the widget's port of the consoles'
 * `webui/static/shared/contact.js` (same checks, same codes, same markup).
 *
 * A QuestionView with qtype "contact" carries `fields` ("name" / "email" /
 * "phone"), `required` and `text`. The form posts to
 * POST /interviews/{id}/contact (api.ts `submitContact`), never as a chat
 * message, so the details stay out of the transcript. The checks mirror
 * domain/contact.py for quick feedback; the server's are the ones that count.
 */
import type { QuestionView } from "./types";

const EMAIL = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"_]+(?:\.[^\s@<>()[\]\\,;:"_]+)*\.\p{L}{2,}$/u;
const PHONE_SEPARATORS = /[\s().\-/‐-―]/g;
const NAME_MAX = 200;
const EMAIL_MAX = 254;

/** Codes with a message of their own (`contact.err.<code>`). */
export const CONTACT_ERRORS = new Set([
  "invalid_name", "invalid_email", "invalid_phone", "missing_email", "missing_phone",
  "empty", "required", "failed",
]);

export interface ContactValues {
  name?: string;
  email?: string;
  phone?: string;
}

export interface ContactResult {
  ok: boolean;
  code?: string;
  status?: number;
}

const FIELD: Record<string, { type: string; autocomplete: AutoFill; inputMode: string; max: number }> = {
  name: { type: "text", autocomplete: "name", inputMode: "text", max: NAME_MAX },
  email: { type: "email", autocomplete: "email", inputMode: "email", max: EMAIL_MAX },
  phone: { type: "tel", autocomplete: "tel", inputMode: "tel", max: 40 },
};

export const isContactView = (v: QuestionView | null | undefined): boolean =>
  !!v && v.qtype === "contact";

/** One field's error code ("" when fine). Empty values: see `formError`. */
export function fieldError(field: string, raw: string): string {
  const value = String(raw || "").trim();
  if (!value) return "";
  if (field === "name") return value.length > NAME_MAX ? "invalid_name" : "";
  if (field === "email") return value.length <= EMAIL_MAX && EMAIL.test(value) ? "" : "invalid_email";
  if (field === "phone") {
    const compact = value.normalize("NFKC").replace(PHONE_SEPARATORS, "");
    if (!/^\+?\d+$/.test(compact)) return "invalid_phone";
    const digits = compact.replace(/^\+/, "").length;
    return digits >= 7 && digits <= 15 ? "" : "invalid_phone";
  }
  return "";
}

/** What a whole form lacks ("" when it can be sent). */
export function formError(view: QuestionView, values: ContactValues): string {
  const asks = new Set(view.fields ?? []);
  if (view.required) {
    if (asks.has("email") && !values.email) return "missing_email";
    if (asks.has("phone") && !values.phone) return "missing_phone";
  }
  if (!values.email && !values.phone) return "empty";
  return "";
}

const FIELD_OF: Record<string, string> = {
  invalid_name: "name", invalid_email: "email", missing_email: "email",
  invalid_phone: "phone", missing_phone: "phone",
};

let formCount = 0;

/**
 * Build the form. `onSubmit` / `onDecline` resolve to `{ ok }` or
 * `{ ok: false, code }`; the form locks itself once one succeeds.
 */
export function buildContactForm(
  view: QuestionView,
  t: (key: string) => string,
  handlers: {
    onSubmit: (values: ContactValues) => Promise<ContactResult>;
    onDecline: () => Promise<ContactResult>;
  }
): { el: HTMLFormElement; focus: () => void } {
  const uid = `dtro-contact${++formCount}`;
  const form = document.createElement("form");
  form.className = "dtro-contact";
  form.setAttribute("part", "contact-form");
  form.noValidate = true;
  form.setAttribute("aria-describedby", `${uid}-privacy`);

  const inputs: Record<string, HTMLInputElement> = {};
  const errors: Record<string, HTMLElement> = {};
  for (const field of view.fields ?? []) {
    const spec = FIELD[field];
    if (!spec) continue;
    const row = document.createElement("div");
    row.className = "dtro-contact-field";
    const label = document.createElement("label");
    label.htmlFor = `${uid}-${field}`;
    label.textContent = t(`contact.${field}`);
    const optional = field === "name" || !view.required;
    if (optional) {
      const tag = document.createElement("span");
      tag.className = "dtro-contact-optional";
      tag.textContent = ` (${t("contact.optional")})`;
      label.append(tag);
    }
    const input = document.createElement("input");
    input.id = `${uid}-${field}`;
    input.name = field;
    input.type = spec.type;
    input.autocomplete = spec.autocomplete;
    input.inputMode = spec.inputMode;
    input.maxLength = spec.max;
    input.enterKeyHint = "next";
    if (field !== "name") input.spellcheck = false;
    if (field === "email") input.placeholder = t("contact.email_placeholder");
    if (field === "phone") input.placeholder = t("contact.phone_placeholder");
    if (!optional) input.required = true;
    const error = document.createElement("p");
    error.className = "dtro-contact-error";
    error.id = `${uid}-${field}-error`;
    error.hidden = true;
    input.setAttribute("aria-describedby", error.id);
    row.append(label, input, error);
    form.append(row);
    inputs[field] = input;
    errors[field] = error;
  }
  const inputList = Object.values(inputs);
  const lastInput = inputList[inputList.length - 1];
  if (lastInput) lastInput.enterKeyHint = "send";

  const privacy = document.createElement("p");
  privacy.className = "dtro-contact-privacy";
  privacy.id = `${uid}-privacy`;
  privacy.textContent = t("contact.privacy");
  const status = document.createElement("p");
  status.className = "dtro-contact-status";
  status.setAttribute("role", "alert");
  const actions = document.createElement("div");
  actions.className = "dtro-contact-actions";
  const submit = document.createElement("button");
  submit.type = "submit";
  submit.className = "dtro-chip dtro-contact-submit";
  submit.textContent = t("contact.submit");
  actions.append(submit);
  let decline: HTMLButtonElement | null = null;
  if (!view.required) {
    decline = document.createElement("button");
    decline.type = "button";
    decline.className = "dtro-chip subtle";
    decline.textContent = t("contact.decline");
    actions.append(decline);
  }
  form.append(privacy, status, actions);

  const showFieldError = (field: string, code: string) => {
    const input = inputs[field];
    const error = errors[field];
    if (!input || !error) return;
    const message = code ? t(`contact.err.${code}`) : "";
    input.setAttribute("aria-invalid", code ? "true" : "false");
    input.setCustomValidity(message);
    error.textContent = message;
    error.hidden = !code;
  };
  for (const [field, input] of Object.entries(inputs)) {
    input.addEventListener("blur", () => showFieldError(field, fieldError(field, input.value)));
    input.addEventListener("input", () => {
      if (input.getAttribute("aria-invalid") === "true" && !fieldError(field, input.value)) {
        showFieldError(field, "");
      }
      status.textContent = "";
    });
  }

  const setBusy = (on: boolean) => {
    form.querySelectorAll<HTMLInputElement | HTMLButtonElement>("input, button").forEach((c) => {
      c.disabled = on;
    });
  };
  const lock = (text: string) => {
    form.dataset.state = "done";
    setBusy(true);
    const done = document.createElement("p");
    done.className = "dtro-contact-done";
    done.textContent = text;
    actions.replaceChildren(done);
  };
  const fail = (code: string) => {
    setBusy(false);
    const field = FIELD_OF[code];
    if (field && inputs[field]) {
      showFieldError(field, code);
      inputs[field]!.focus();
      return;
    }
    status.textContent = t(`contact.err.${CONTACT_ERRORS.has(code) ? code : "failed"}`);
  };

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    if (form.dataset.state === "done") return;
    const values: ContactValues = {};
    let firstBad: HTMLInputElement | null = null;
    for (const [field, input] of Object.entries(inputs)) {
      const value = input.value.trim();
      (values as Record<string, string>)[field] = value;
      const code = fieldError(field, value);
      showFieldError(field, code);
      if (code && !firstBad) firstBad = input;
    }
    if (firstBad) {
      firstBad.focus();
      return;
    }
    const missing = formError(view, values);
    if (missing) {
      fail(missing);
      return;
    }
    status.textContent = "";
    setBusy(true);
    const res = await handlers.onSubmit(values);
    if (res.ok) lock(t("contact.shared"));
    else fail(res.code || "failed");
  });
  decline?.addEventListener("click", async () => {
    if (form.dataset.state === "done") return;
    setBusy(true);
    const res = await handlers.onDecline();
    if (res.ok) lock(t("contact.declined"));
    else fail(res.code || "failed");
  });

  return { el: form, focus: () => inputList[0]?.focus() };
}
